import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LineReveal } from "./Reveal";
import { IMAGES, STATS } from "@/lib/site-data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden grid-bg"
    >
      {/* Parallax hardware backdrop */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={IMAGES.hero}
          alt="Macro of an embedded circuit board"
          className="h-full w-full object-cover opacity-70"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-[#070708]"
      />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-[#070708] via-[#070708]/40 to-[#070708]/70" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 min-h-[100svh] flex flex-col justify-center pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="overline">Quantinium Limited</span>
          <span className="h-px w-12 bg-cyan-400/50" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Est. Embedded Systems · UK
          </span>
        </motion.div>

        <h1 className="font-display font-700 tracking-tighter leading-[0.92] text-white text-[13vw] sm:text-[10vw] md:text-[8.2vw] lg:text-[7.6rem]">
          <LineReveal lines={["Engineering", "the invisible"]} delay={0.45} />
          <span className="block text-cyan-400">
            <LineReveal lines={["systems inside."]} delay={0.75} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-xl text-lg leading-relaxed text-zinc-400"
        >
          Three decades designing embedded firmware and full systems — from the
          silicon on the board to the cloud pipeline behind it. Let us design your
          embedded software, or your entire system.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-2 bg-cyan-400 text-black font-mono text-[13px] uppercase tracking-[0.12em] px-7 py-4 hover:bg-cyan-300 transition-colors duration-300 clip-frame-sm"
          >
            Commission a build
            <ArrowDown className="h-4 w-4 group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 border border-white/15 text-white font-mono text-[13px] uppercase tracking-[0.12em] px-7 py-4 hover:border-white/40 transition-colors duration-300"
          >
            View capabilities
          </button>
        </motion.div>

        {/* Stat bar */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.55, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-stats"
          className="mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
        >
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`py-6 pr-6 ${i !== 0 ? "md:pl-8" : ""} border-b md:border-b-0 border-white/10 ${
                i < 3 ? "md:border-r" : ""
              }`}
            >
              <div className="font-display font-700 text-2xl md:text-3xl text-white tracking-tight">
                {s.value}
              </div>
              <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
